import { cn } from "@/utils/theme";
import { Button, Icon } from "@elaniintc/goes-design-system";
import { Handle, Position } from "@xyflow/react";
import TooltipWrapper from "../../Tooltip";

interface WaitInputInfo {
  id?: string; // UUID del input desde el backend
  name: string; // Nombre visible
  hasConnection: boolean; // Si tiene al menos una conexión
}

interface Props {
  node_id: string;
  inputs: WaitInputInfo[];
  isExecutionView?: boolean;
  isSelected?: boolean;
  isDeleting?: boolean;
  isValid?: boolean;
  hasError?: boolean;
}

/**
 * Componente que renderiza múltiples handles de entrada para el nodo Wait.
 * Cada handle tiene un ID en formato `input-{index}` (1-indexed).
 */
const MultiInputs = ({
  node_id,
  inputs,
  isExecutionView = false,
  isDeleting,
  isSelected,
  hasError,
  isValid,
}: Props) => {
  return (
    <>
      {inputs.map((input, index) => {
        // Handle ID en formato consistente: input-1, input-2, etc.
        const handleId = `input-${index + 1}`;

        // Calcular posición vertical
        let topPercent = `${(100 / (inputs.length + 1)) * (index + 1)}%`;

        // Ajustes especiales para 3 inputs
        if (inputs.length === 3 && index === 0) {
          topPercent = "15%";
        }
        if (inputs.length === 3 && index === 1) {
          topPercent = "50%";
        }
        if (inputs.length === 3 && index === 2) {
          topPercent = "85%";
        }

        const isConnected = input.hasConnection;

        // En vista de ejecución, ocultar inputs sin conexión
        if (!isConnected && isExecutionView) return null;

        return (
          <div
            key={`${node_id}-${handleId}`}
            className="bg-transparent"
            style={{
              position: "absolute",
              top: topPercent,
              left: -20,
              transform: "translateY(-50%)",
              width: "20px",
              height: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "8px",
            }}
          >
            {/* Botón de "+" cuando no hay conexión */}
            {!isExecutionView && !isConnected && (
              <>
                <Button
                  variant="quiet"
                  size="xs"
                  className="rounded-md border-[1.5px] size-8 no-drag pointer-events-none"
                  aria-label="Input handle"
                  style={{
                    pointerEvents: "none",
                    marginRight: "6px",
                  }}
                >
                  <Icon type="far" name="plus" />
                </Button>
                <div
                  className={cn(
                    "h-px border border-neutral pointer-events-none",
                    isSelected && "border-action-quiet",
                    isValid && "border-success-quiet",
                    (isDeleting || hasError) && "border-danger-quiet",
                  )}
                  style={{ width: "12px", marginLeft: "-22px" }}
                />
              </>
            )}

            {/* Handle de React Flow */}
            <Handle
              type="target"
              position={Position.Left}
              id={handleId}
              className="!rounded-none !border-none !bg-transparent z-40 !left-0 no-drag"
              style={{
                position: "relative",
                top: "50%",
                transform: "translateY(-50%)",
                width: "20px",
              }}
            >
              {/* Indicador visual del handle */}
              <div
                className={cn(
                  "w-[3px] h-3.5 bg-on-disabled rounded-l-full absolute right-px pointer-events-none handle-indicator",
                  isSelected && "bg-action-quiet",
                  (isDeleting || hasError) && "bg-danger-quiet",
                  isValid && "bg-success-quiet",
                )}
              />
              {/* Línea de conexión */}
              <div
                className={cn(
                  "w-[20px] h-px border border-neutral absolute top-1.5 -z-10 -left-0.5 pointer-events-none",
                  isSelected && "border-action-quiet",
                  isValid && "border-success-quiet",
                )}
              />
            </Handle>

            {/* Label del input - solo mostrar cuando hay conexión */}
            {!isExecutionView && isConnected && (
              <TooltipWrapper title={input.name}>
                <div
                  className={cn(
                    "absolute pointer-events-none",
                    "nopan bg-neutral-object rounded-lg w-auto border border-neutral",
                  )}
                  style={{
                    left: "-8px",
                    top: "50%",
                    transform: "translateX(-100%) translateY(-50%)",
                  }}
                >
                  <div className="edge-label text-body-loud min-w-[40px] max-w-[148px] px-1.5 text-[12px] whitespace-nowrap truncate">
                    {input.name}
                  </div>
                </div>
              </TooltipWrapper>
            )}
          </div>
        );
      })}
    </>
  );
};

export default MultiInputs;
